import React, { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../../auth/authConfig";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignInAlt, faShieldAlt, faExclamationTriangle, faSync } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from "../../auth/AuthContext";
import { motion } from 'framer-motion';
import Loader from "../common/Loader";

// Import logos and background
import companyLogo from '../../assets/images/wedl_logo.svg';
import backgroundSvg from '../../assets/images/wedl_hintergrund.svg';
import './Login.css';

const Login = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error, hasGroupAccess, userGroups, logout } = useAuth();
  
  // If user is authenticated AND has group access, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated && hasGroupAccess && !isLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, hasGroupAccess, isLoading, navigate]);

  const handleLogin = () => {
    console.log('[LOGIN] Starte Login-Prozess');
    
    // Verwende immer Redirect (kein Popup mehr)
    instance.loginRedirect(loginRequest);
  };

  // NEU: Re-Authentication für Gruppenberechtigung
  const handleReAuthenticate = async () => {
    console.log('[LOGIN] Re-Authentication gestartet...');
    
    try {
      // Logout aus der aktuellen Session
      await logout();
      
      // Kurz warten, dann neuen Login starten
      setTimeout(() => {
        // Force Login mit prompt=login um neue Token zu erhalten
        instance.loginRedirect({
          ...loginRequest,
          prompt: 'login' // WICHTIG: Erzwingt neue Anmeldung
        });
      }, 1000);
      
    } catch (error) {
      console.error('[LOGIN] Re-Authentication Fehler:', error);
      // Fallback: Normale Anmeldung
      handleLogin();
    }
  };

  if (isLoading) {
    return (
      <div className="app-fullscreen-loader">
        <Loader size="large" text="System wird initialisiert..." />
      </div>
    );
  }

  // GRUPPENBERECHTIGUNG FEHLER ANZEIGEN
  const showGroupError = !!(error && error.includes('Berechtigung'));
  // FIXED: Zeige Re-Auth nur wenn tatsächlich authentifiziert aber ohne Zugriff
  const isLoggedInButNoAccess = isAuthenticated && !hasGroupAccess && !isLoading;

  return (
    <div className="app-login-screen">
      <div className="app-background">
        <div 
          className="wedl-background-container"
          style={{ backgroundImage: `url(${backgroundSvg})` }}
        ></div>
      </div>

      <div className="app-login-container">
        <div className="app-login-content">
          <motion.div 
            className="app-logo-container"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <div className="app-logo">
              <motion.img 
                src={companyLogo} 
                alt="WEDL Logo"
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ 
                  type: "spring",
                  stiffness: 100,
                  delay: 0.5
                }}
                whileHover={{ scale: 1.05 }}
              />
            </div>
          </motion.div>
          
          <motion.div 
            className="app-brand-text"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <h1>Supplier Portal</h1>
            <h2>Anwendung für Fragebögen und Artikelerfassung</h2>
          </motion.div>
          
          <motion.div 
            className="app-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            <div className="app-card-header">
              <h3>Anmeldung</h3>
              <p>Melden Sie sich mit Ihrem Unternehmenskonto an.</p>
            </div>
            
            <div className="app-card-body">
              {!showGroupError && (
                <div className="app-security-badge">
                  <FontAwesomeIcon icon={faShieldAlt} className="app-security-icon" />
                  <span>Sichere Verbindung</span>
                </div>
              )}
              
              {/* GRUPPENFEHLER ANZEIGE */}
              {showGroupError && (
                <motion.div 
                  className="app-group-error"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{
                    padding: '1rem',
                    marginBottom: '1rem',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    color: '#dc2626'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <strong>Zugriff verweigert</strong>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.9rem' }}>
                    Sie haben keine Berechtigung für diese Anwendung.
                    <br />
                    Erforderliche Gruppe: <strong>"Lieferantenfragebogenzugriff"</strong>
                  </p>
                  {userGroups.length > 0 && (
                    <details style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                      <summary>Ihre Gruppen anzeigen</summary>
                      <ul style={{ margin: '0.5rem 0 0 1rem' }}>
                        {userGroups.map((group, index) => (
                          <li key={index}>{group}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem' }}>
                    Wenden Sie sich an Ihren Administrator.
                  </p>
                </motion.div>
              )}
              
              {/* HAUPTBUTTON: Login oder Re-Authentication */}
              {!isLoggedInButNoAccess ? (
                // Normaler Login Button
                <motion.button 
                  className="app-login-button"
                  onClick={handleLogin}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FontAwesomeIcon icon={faSignInAlt} className="app-button-icon" />
                  <span>Mit Microsoft anmelden</span>
                </motion.button>
              ) : (
                // Re-Authentication Button wenn bereits angemeldet aber keine Berechtigung
                <motion.button 
                  className="app-login-button app-reauth-button"
                  onClick={handleReAuthenticate}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: '0 6px 15px rgba(245, 158, 11, 0.25)'
                  }}
                >
                  <FontAwesomeIcon icon={faSync} className="app-button-icon" />
                  <span>Berechtigung erneut prüfen</span>
                </motion.button>
              )}
              
              {/* ZUSÄTZLICHER BUTTON: Normale Anmeldung wenn Re-Auth gezeigt wird */}
              {isLoggedInButNoAccess && (
                <motion.button 
                  className="app-login-button app-secondary-button"
                  onClick={handleLogin}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    background: 'transparent',
                    border: '2px solid #6b7280',
                    color: '#6b7280',
                    marginTop: '0.75rem',
                    boxShadow: 'none'
                  }}
                >
                  <FontAwesomeIcon icon={faSignInAlt} className="app-button-icon" />
                  <span>Als anderer Benutzer anmelden</span>
                </motion.button>
              )}
            </div>
          </motion.div>
          
          <motion.div 
            className="app-login-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            <p>© {new Date().getFullYear()} WEDL Handels GmbH • Daniel Auer</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;